import {useSyncExternalStore} from 'react';

/**
 * Beamer ("What's new") in the shell. The embed script is inserted by
 * `public/consentAwareAnalyticsInjector.js`, which also sets
 * `window.beamer_config`; this module only initializes it and tracks the unread
 * post count so the header can show a badge. The shell only ships to SaaS, so
 * the Beamer product is fixed to `saas`.
 */

type BeamerApi = {
  init(): void;
  show(): void;
  update(config: {
    deployment_type?: 'saas' | 'server';
    callback?: (newPosts: number) => void;
    onopen?: () => void;
  }): void;
};

declare global {
  interface Window {
    Beamer?: BeamerApi;
  }
}

const BEAMER_INIT_RETRIES = 10;
const BEAMER_INIT_RETRY_MS = 500;

type BeamerState = {
  available: boolean | null;
  newPosts: number;
};

let state: BeamerState = {available: null, newPosts: 0};
const listeners = new Set<() => void>();

function setState(next: Partial<BeamerState>): void {
  state = {...state, ...next};
  for (const listener of listeners) {
    listener();
  }
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function getState(): BeamerState {
  return state;
}

export function useBeamerState(): BeamerState {
  return useSyncExternalStore(subscribe, getState, getState);
}

const wait = (ms: number) =>
  new Promise<void>(resolve => setTimeout(resolve, ms));

let initPromise: Promise<boolean> | undefined;
let generation = 0;

/**
 * Resolves true once Beamer has loaded and reported its unread count, false if
 * the embed never appeared (blocked by consent, an ad blocker, or not
 * configured). Safe to call repeatedly; the first call wins.
 */
export function beamerInit(): Promise<boolean> {
  initPromise ??= (async () => {
    const startedIn = generation;
    let tries = 0;
    while (
      startedIn === generation &&
      globalThis.window?.Beamer == null &&
      tries < BEAMER_INIT_RETRIES
    ) {
      tries++;
      await wait(BEAMER_INIT_RETRY_MS);
    }
    if (startedIn !== generation || globalThis.window == null) {
      return false;
    }
    const beamer = window.Beamer;
    if (beamer == null) {
      setState({available: false});
      return false;
    }
    return new Promise<boolean>(resolve => {
      beamer.update({
        deployment_type: 'saas',
        callback: newPosts => {
          setState({available: true, newPosts});
          resolve(true);
        },
        onopen: () => {
          setState({newPosts: 0});
        },
      });
      beamer.init();
    });
  })();
  return initPromise;
}

export async function openBeamer(): Promise<void> {
  if (await beamerInit()) {
    window.Beamer?.show();
  }
}

/** Test seam: module state is per-page, so suites must clear it. */
export function resetBeamerForTests(): void {
  state = {available: null, newPosts: 0};
  listeners.clear();
  initPromise = undefined;
  generation++;
}
